const REQUIRED_TYPES: Readonly<Record<string, readonly string[]>> = {
  DISTRIBUTION: ['RATE_PLAN', 'CHANNEL_MAPPING', 'PROMOTION'],
  PMS: ['PMS_RESERVATION'],
  WALLET: ['WALLET_TRANSACTION'],
  IAM: ['IAM_USER'],
  WBS: ['BOOKING'],
  ASSET: ['ASSET_ENTITY'],
  REPORTING: ['REPORT']
}

export interface SupportContextCapabilities {
  enabledRefTypes: string[]
  maxAffectedEntities?: number
  maxContextHints?: number
}

interface ContextSelection {
  affectedEntities: readonly unknown[]
  relatedEntities: readonly unknown[]
  relatedPromotions: readonly unknown[]
  relatedWalletTransactions?: readonly unknown[]
  relatedDistribution: readonly { refType: string }[]
  attachTrace: boolean
}

interface ContextReadiness {
  checking: boolean
  affectedLimitExceeded: boolean
  contextLimitExceeded: boolean
  maxAffectedEntities: number
  maxContextHints: number
}

function negotiatedLimit(value: number | undefined): number {
  // Twenty is the stable protocol ceiling, also used by older test/clients
  // that omit these additive capability fields. Never increase that ceiling.
  return typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? Math.min(value, 20)
    : 20
}

export function supportContextReadiness(
  values: ContextSelection,
  capabilities: SupportContextCapabilities | undefined,
  loading: boolean
): ContextReadiness {
  const enabled = capabilities?.enabledRefTypes || []
  const selectedCount =
    values.relatedEntities.length +
    values.relatedPromotions.length +
    (values.relatedWalletTransactions?.length || 0) +
    values.relatedDistribution.length +
    Number(values.attachTrace)
  const contextCount =
    (enabled.includes('ASSET_ENTITY') ? values.relatedEntities.length : 0) +
    (enabled.includes('PROMOTION') ? values.relatedPromotions.length : 0) +
    (enabled.includes('WALLET_TRANSACTION')
      ? values.relatedWalletTransactions?.length || 0
      : 0) +
    values.relatedDistribution.filter(({ refType }) =>
      enabled.includes(refType)
    ).length +
    Number(values.attachTrace)
  const maxAffectedEntities = negotiatedLimit(capabilities?.maxAffectedEntities)
  const maxContextHints = negotiatedLimit(capabilities?.maxContextHints)
  return {
    checking:
      loading && (selectedCount > 0 || values.affectedEntities.length > 0),
    affectedLimitExceeded: values.affectedEntities.length > maxAffectedEntities,
    contextLimitExceeded: contextCount > maxContextHints,
    maxAffectedEntities,
    maxContextHints
  }
}

export function unavailableContextForProduct(
  productScope: string,
  enabledTypes?: readonly string[]
): boolean {
  if (!enabledTypes) return false
  const required = REQUIRED_TYPES[productScope.trim().toUpperCase()]
  return Boolean(required?.every((kind) => !enabledTypes.includes(kind)))
}
